import { useDisclosure } from '@chakra-ui/react'
import cn from 'clsx'
import { FC, useEffect, useState } from 'react'

import { Layout } from '@/components/ui'
import Spinner from '@/components/ui/spinner/Spinner'

import { useAuth } from '@/auth/useAuth'
import { useTaskWatch } from '@/hooks/useTaskWatch'
import { useFormUploaderTaskSession } from '@/store/features/formUploader/useFormUploaderTaskSession'

import { useHome } from '../Home/hooks/useHome'

import FileOriginalColumn from './components/FileOriginalColumn'
import FileProcessPanel from './components/FileProcessPanel'
import FileSidebar from './components/FileSidebar'
import FileTranslationColumn from './components/FileTranslationColumn'
import UploadOriginalText from './components/UploadOriginalText'
import TranslateError from './components/components/TranslateError'
import { getFileDerivedData } from './helpers/getFileDerivedData'
import { getFilePageRenderState } from './helpers/getFilePageRenderState'
import { useFile } from './hooks/useFile'
import { useFileFormHeaderSync } from './hooks/useFileFormHeaderSync'
import { useFileSaveAndClose } from './hooks/useFileSaveAndClose'
import { useFileSrtUpload } from './hooks/useFileSrtUpload'
import { useFileTabs } from './hooks/useFileTabs'

export interface ITranslateParams {
	targetLang: string
	sourceLang: string
}

const File: FC = () => {
	const {
		shotlist,
		translateShotlist,
		isShotlistLoading,
		refetch,
		isGetError
	} = useFile()

	const { watchTask, clearWatch } = useTaskWatch()

	const [isTranslateLoading, setIsTranslateLoading] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isTranslateError, setIsTranslateError] = useState(false)
	const [lastTranslateParams, setLastTranslateParams] =
		useState<ITranslateParams | null>(null)
	const [isChoosingLanguage, setIsChoosingLanguage] = useState(false)
	const [targetLang, setTargetLang] = useState<string | null>(null)
	const [isTranscribing, setIsTranscribing] = useState(false)
	const [translatingLang, setTranslatingLang] = useState<string | null>(null)

	const selectLanguageDialog = useDisclosure()
	const selectSrtDialog = useDisclosure()

	const {
		selectedSrtLang,
		setSelectedSrtLang,
		selectedSrtFile,
		setSelectedSrtFile,
		isSrtUploading,
		onUploadSrtShotlist
	} = useFileSrtUpload({
		taskId: shotlist?.id,
		refetchShotlist: refetch,
		onCloseSrtDialog: selectSrtDialog.onClose
	})

	const { languages } = useHome()
	const { isInt } = useAuth()

	const { handleSaveAndClose, goHome } = useFileSaveAndClose()

	const { leftTab, rightTab, setLeftTab, setRightTab } = useFileTabs({
		shotlist,
		isTranslateLoading,
		lastTranslateTargetLang: lastTranslateParams?.targetLang
	})

	const {
		selectedPlatform,
		selectedPlatformList,
		selectedTranscribingLang,
		setSelectedPlatformList,
		setSelectedTranscribingLang
	} = useFormUploaderTaskSession(shotlist?.id)

	useFileFormHeaderSync({
		shotlist,
		selectedPlatformList,
		setSelectedPlatformList
	})

	const {
		isTranslation,
		availableShotlistLanguages,
		allPlatformsLanguages,
		leftRenderLanguage,
		rightRenderLanguage,
		translationLanguages,
		noTexts,
		originalSourceName
	} = getFileDerivedData({
		shotlist,
		languages,
		leftTab,
		rightTab,
		isInt
	})

	const isProcessingText = isTranscribing || isSrtUploading
	const hasAvailableShotlistLanguages = availableShotlistLanguages.length > 0
	const hasOriginalContent = Boolean(shotlist?.original?.content)
	const hasTranslations = Boolean(shotlist?.translations?.length)

	const filePageRenderState = getFilePageRenderState({
		noTexts,
		hasAvailableShotlistLanguages,
		hasOriginalContent,
		hasTranslations,
		isProcessingText,
		isTranslateLoading
	})

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [])

	const handleFirstTranslate = async (targetLang: string) => {
		if (
			!shotlist ||
			!shotlist.original ||
			shotlist.original.language === targetLang
		)
			return

		setIsTranslateLoading(true)
		try {
			await translateShotlist({
				target_language: targetLang,
				source_language: shotlist.original.language
			})
			setRightTab(targetLang)
			await refetch()
		} catch (error) {
			console.error(error)
			setIsTranslateError(true)
		} finally {
			setIsTranslateLoading(false)
		}
	}

	if (isGetError) {
		return (
			<div className='flex h-screen items-center justify-center'>
				<TranslateError
					tryCallback={() => window.location.reload()}
					closeCallback={goHome}
					closeButtonText={'Main Page'}
				/>
			</div>
		)
	}

	return (
		<Layout className='relative'>
			{(isLoading || isShotlistLoading) && <Spinner />}
			<div className={cn('flex', isLoading ? 'opacity-[0.5]' : 'opacity-1')}>
				<FileSidebar
					video={shotlist?.video}
					isShotlistLoading={isShotlistLoading}
					onSaveAndClose={handleSaveAndClose}
					source={shotlist?.source}
				/>

				{filePageRenderState === 'empty-processing' && (
					<FileProcessPanel>
						Transcribing from{' '}
						{selectedTranscribingLang?.language || selectedSrtFile?.name}
					</FileProcessPanel>
				)}

				{filePageRenderState === 'empty' && (
					<UploadOriginalText
						taskId={shotlist?.id}
						languages={languages}
						onUploadSrt={onUploadSrtShotlist}
						selectedTranscribingLang={selectedTranscribingLang}
						setSelectedTranscribingLang={setSelectedTranscribingLang}
						selectedSrtLang={selectedSrtLang}
						setSelectedSrtLang={setSelectedSrtLang}
						setSelectedSrtFile={setSelectedSrtFile}
						selectLanguageDialog={selectLanguageDialog}
						selectSrtDialog={selectSrtDialog}
						isTranslation={shotlist?.translations?.length > 0}
						clearWatch={clearWatch}
						refetchShotlist={refetch}
						setIsTranscribing={setIsTranscribing}
						watchTask={watchTask}
					/>
				)}

				{filePageRenderState !== 'empty-processing' &&
					filePageRenderState !== 'empty' && (
						<>
							{filePageRenderState === 'text-processing' ? (
								<FileProcessPanel>
									Transcribing from{' '}
									{selectedTranscribingLang?.language || selectedSrtFile?.name}
								</FileProcessPanel>
							) : (
								<FileOriginalColumn
									shotlist={shotlist}
									hasAvailableShotlistLanguages={hasAvailableShotlistLanguages}
									leftRenderLanguage={leftRenderLanguage}
									setIsLoading={setIsLoading}
									refetch={refetch}
									translationLanguages={translationLanguages}
									leftTab={leftTab}
									setLeftTab={setLeftTab}
									isTranslateLoading={isTranslateLoading}
									setIsTranslateLoading={setIsTranslateLoading}
									setRightTab={setRightTab}
									isTranslateError={isTranslateError}
									setIsTranslateError={setIsTranslateError}
									lastTranslateParams={lastTranslateParams}
									setLastTranslateParams={setLastTranslateParams}
									isChoosingLanguage={isChoosingLanguage}
									setIsChoosingLanguage={setIsChoosingLanguage}
									targetLang={targetLang}
									setTargetLang={setTargetLang}
									originalSourceName={originalSourceName}
									languages={languages}
									onUploadSrt={onUploadSrtShotlist}
									selectedTranscribingLang={selectedTranscribingLang}
									setSelectedTranscribingLang={setSelectedTranscribingLang}
									selectedSrtLang={selectedSrtLang}
									setSelectedSrtLang={setSelectedSrtLang}
									setSelectedSrtFile={setSelectedSrtFile}
									selectLanguageDialog={selectLanguageDialog}
									selectSrtDialog={selectSrtDialog}
									clearWatch={clearWatch}
									refetchShotlist={refetch}
									setIsTranscribing={setIsTranscribing}
									watchTask={watchTask}
								/>
							)}

							<FileTranslationColumn
								filePageRenderState={filePageRenderState}
								allPlatformsLanguages={allPlatformsLanguages}
								handleFirstTranslate={handleFirstTranslate}
								setIsChoosingLanguage={setIsChoosingLanguage}
								setTranslatingLang={setTranslatingLang}
								translatingLang={translatingLang}
								selectedPlatform={selectedPlatform}
								isTranslation={isTranslation}
								isTranslateLoading={isTranslateLoading}
								isTranslateError={isTranslateError}
								rightRenderLanguage={rightRenderLanguage}
								setIsLoading={setIsLoading}
								refetch={refetch}
								setRightTab={setRightTab}
								translationLanguages={translationLanguages}
								rightTab={rightTab}
								originalLang={shotlist?.original?.language}
								setIsTranslateLoading={setIsTranslateLoading}
								setIsTranslateError={setIsTranslateError}
								lastTranslateParams={lastTranslateParams}
								setLastTranslateParams={setLastTranslateParams}
								isChoosingLanguage={isChoosingLanguage}
								targetLang={targetLang}
								setTargetLang={setTargetLang}
								originalSourceName={originalSourceName}
							/>
						</>
					)}
			</div>
		</Layout>
	)
}

export default File
